import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useParams } from "react-router";
import { Minimize2, MoreHorizontal, Pin, Search, Send, X } from "lucide-react";
import { toast } from "sonner";
import { ContactChurnedBadge } from "@/components/journey/ContactChurnedBadge";
import { HermesDrawer } from "@/components/hermes/HermesDrawer";
import { useHermes } from "@/components/hermes/HermesProvider";
import { ConfirmDialog } from "@/components/shell/ConfirmDialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useAccountWorkspace } from "@/lib/account-workspace";
import {
  type ChatTimelineItem,
  type JourneyPlanItem,
} from "@/lib/chat-timeline";
import {
  buildInstructionByStepId,
  getCustomerBindingPlan,
  listCustomerBindings,
  type JourneyPlanStep,
} from "@/lib/journey-bindings";
import {
  JourneyApiError,
  getJourney,
  type JourneyNode,
} from "@/lib/journey-api";
import { timelineZones } from "@/lib/timeline-zones";
import { lookupTouchInstruction } from "@/lib/journey-touch-display";
import {
  ApiError,
  formatLastInboundHint,
  getWecomExternalContact,
  listWecomExternalContacts,
  mergeAndSortPinnedContact,
  restoreElementScrollTop,
  updateWecomExternalContact,
  type WecomExternalContact,
} from "@/lib/wecom-external-contacts";
import {
  listWecomInboundEvents,
  type WecomInboundEvent,
} from "@/lib/wecom-inbound-events";
import {
  cancelOutboundMessage,
  createWecomOutboundMessage,
  listOutboundPeers,
  listWecomOutboundMessages,
  reachOrchestrationResultFromTaskMeta,
  reachOrchestrationToastMessage,
  replayPlanStep,
  schedulePlanStep,
  sendJourneyPlanStepNow,
  triggerReachOrchestration,
  type OutboundPeer,
  type WecomOutboundMessage,
} from "@/lib/wecom-outbound-messages";
import { contactHasActiveReachDraft, isStepDrafting } from "@/lib/reach-draft-indicator";
import {
  listWecomUsers,
  wecomUserNameByUserid,
  type WecomUser,
} from "@/lib/wecom-users";
import {
  reachDraftScope,
  useBackgroundTasksOptional,
  type BackgroundTask,
} from "@/lib/background-tasks";
import { cn } from "@/lib/utils";
import { CustomerProfilePanel } from "./CustomerProfilePanel";
import { GroupSendRecipientsDrawer } from "./GroupSendRecipientsDrawer";
import { InboundEventTimeline } from "./InboundEventTimeline";
import { SendPlanStepDialog } from "./SendPlanStepDialog";
import { useCustomerChat } from "./CustomerChatProvider";
import { WecomUserMultiSelect } from "./WecomUserMultiSelect";

function ContactAvatar({
  name,
  avatarUrl,
  className,
}: {
  name: string | null;
  avatarUrl: string | null;
  className?: string;
}) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt=""
        className={cn("size-10 shrink-0 rounded-full object-cover", className)}
        referrerPolicy="no-referrer"
      />
    );
  }
  return (
    <span
      className={cn(
        "flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-sm text-muted-foreground",
        className,
      )}
    >
      {(name || "?").slice(0, 1)}
    </span>
  );
}

function notifyReachDraftFinished(task: BackgroundTask, successToast: string) {
  if (task.status === "failed") {
    toast.error(task.error_message || "成稿失败");
    return;
  }
  const result = reachOrchestrationResultFromTaskMeta(task.meta);
  const toastMsg = reachOrchestrationToastMessage(result, successToast);
  if (toastMsg.tone === "success") toast.success(toastMsg.text);
  else toast.message(toastMsg.text);
}

/** 全屏三栏客户聊天窗；仅 mode === fullscreen 时挂载可见 */
export function CustomerChatWindow() {
  const { brandId: brandIdParam } = useParams();
  const { accountId } = useAccountWorkspace();
  const { state, minimize, closeActive, switchContact, setRightTab } = useCustomerChat();
  const { setOpen, setExpanded } = useHermes();
  const brandId = brandIdParam ?? null;

  const [contacts, setContacts] = useState<WecomExternalContact[]>([]);
  const [listPage, setListPage] = useState(1);
  const [listTotal, setListTotal] = useState(0);
  const [listTotalPages, setListTotalPages] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  /** 侧栏搜索框即时输入 */
  const [listQuery, setListQuery] = useState("");
  /** 防抖后的查询词，用于实际请求 */
  const [debouncedListQuery, setDebouncedListQuery] = useState("");
  /** 顶栏跟进人筛选（空 = 全部客户） */
  const [filterFollowUserids, setFilterFollowUserids] = useState<string[]>([]);
  const [pinSavingId, setPinSavingId] = useState<number | null>(null);

  const [activeContact, setActiveContact] = useState<WecomExternalContact | null>(null);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);
  const [wecomUsers, setWecomUsers] = useState<WecomUser[]>([]);
  const [inboundEvents, setInboundEvents] = useState<WecomInboundEvent[]>([]);
  const [inboundLoading, setInboundLoading] = useState(false);
  const [inboundError, setInboundError] = useState<string | null>(null);
  const [outboundMessages, setOutboundMessages] = useState<WecomOutboundMessage[]>([]);
  /** 当前客户绑定下的计划步 / 手册节点（两区时间线） */
  const [planSteps, setPlanSteps] = useState<JourneyPlanStep[]>([]);
  const [journeyNodes, setJourneyNodes] = useState<Pick<JourneyNode, "id" | "offset_days">[]>([]);
  const [planMeta, setPlanMeta] = useState({ journeyId: 0, journeyName: "" });
  const [hasActiveBinding, setHasActiveBinding] = useState(false);
  /** 全量计划步 / 节点 → 执行说明（出站条任务区补洞） */
  const [instructionByStepId, setInstructionByStepId] = useState<Record<number, string>>({});
  const [instructionByNodeId, setInstructionByNodeId] = useState<Record<number, string>>({});
  /** 有绑定但尚未拿到 ready 计划 */
  const [planPending, setPlanPending] = useState(false);
  const [planRevision, setPlanRevision] = useState(0);
  /** 开关开 = 显示全部未来计划；关 = 默认最近 2 条 */
  const [showAllFuturePlans, setShowAllFuturePlans] = useState(false);
  const [composerText, setComposerText] = useState("");
  const [sending, setSending] = useState(false);
  /** 待确认立即发送的计划步 */
  const [sendNowPlan, setSendNowPlan] = useState<JourneyPlanItem | null>(null);
  const [sendNowBusy, setSendNowBusy] = useState(false);
  const [redraftPlan, setRedraftPlan] = useState<JourneyPlanItem | null>(null);
  const [pendingDraftStepId, setPendingDraftStepId] = useState<number | null>(null);
  const [cancelTarget, setCancelTarget] = useState<WecomOutboundMessage | null>(null);
  const [cancelMode, setCancelMode] = useState<"current" | "all" | null>(null);
  const [cancelPeerCount, setCancelPeerCount] = useState(1);
  const [cancellingOutboundId, setCancellingOutboundId] = useState<number | null>(null);
  const [replayTarget, setReplayTarget] = useState<{
    planStepId: number;
    reason: "replay" | "reschedule";
  } | null>(null);
  const [replayBusy, setReplayBusy] = useState(false);
  const [groupPeers, setGroupPeers] = useState<OutboundPeer[]>([]);
  const [groupDrawerOpen, setGroupDrawerOpen] = useState(false);
  /** 已 toast 过的终态任务，避免重连补洞重复提示 */
  const toastedReachTaskIdsRef = useRef<Set<number>>(new Set());
  /** 本会话见过的进行中成稿任务（用于区分「刚完成」与 catch-up 旧终态） */
  const seenActiveReachTaskIdsRef = useRef<Set<number>>(new Set());
  /** 入队时记下成功 toast 文案（按计划步） */
  const pendingSuccessToastRef = useRef<Map<number | "contact", string>>(new Map());
  const timelineScrollRef = useRef<HTMLDivElement>(null);
  /** 左栏联系人列表滚动容器 */
  const listScrollRef = useRef<HTMLDivElement>(null);
  const backgroundTasks = useBackgroundTasksOptional();

  const isFullscreen = state.mode === "fullscreen";
  const prevFullscreenRef = useRef(isFullscreen);

  // 进入全屏时一次性收起 Workspace Hermes；右栏 Hermes 时再 open 以便 embedded 水合。
  // 离开全屏时清空会话内列表过滤（跟进人 / 搜索），避免关闭后再开残留。
  useEffect(() => {
    const wasFullscreen = prevFullscreenRef.current;
    prevFullscreenRef.current = isFullscreen;
    if (!wasFullscreen && isFullscreen) {
      setOpen(false);
      setExpanded(false);
    }
    if (wasFullscreen && !isFullscreen) {
      setListQuery("");
      setDebouncedListQuery("");
      setFilterFollowUserids([]);
    }
  }, [isFullscreen, setOpen, setExpanded]);

  useEffect(() => {
    if (!isFullscreen) return;
    if (state.rightTab === "hermes") {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [isFullscreen, state.rightTab, setOpen]);

  const loadContactList = useCallback(
    async (page: number, q: string, followUserids: string[]) => {
      setListLoading(true);
      setListError(null);
      try {
        // 与客户页默认一致：最近入站 + 账户级置顶优先
        const payload = await listWecomExternalContacts(accountId, {
          page,
          perPage: 20,
          q: q || undefined,
          followUserids: followUserids.length > 0 ? followUserids : undefined,
          sort: "last_activity",
        });
        setContacts(payload.external_contacts ?? []);
        setListPage(payload.pagination?.page ?? page);
        setListTotal(payload.pagination?.total ?? 0);
        setListTotalPages(Math.max(1, payload.pagination?.total_pages ?? 1));
      } catch (caught) {
        setListError(caught instanceof ApiError ? caught.message : "无法加载客户列表");
        setContacts([]);
        setListTotal(0);
      } finally {
        setListLoading(false);
      }
    },
    [accountId],
  );

  const mergeWecomUsers = useCallback((users: WecomUser[]) => {
    setWecomUsers((prev) => {
      const byId = new Map(prev.map((u) => [u.userid, u]));
      let changed = false;
      for (const user of users) {
        const existing = byId.get(user.userid);
        if (
          !existing ||
          existing.name !== user.name ||
          existing.avatar_url !== user.avatar_url
        ) {
          byId.set(user.userid, user);
          changed = true;
        }
      }
      return changed ? Array.from(byId.values()) : prev;
    });
  }, []);

  // 侧栏搜索防抖 ~250ms
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedListQuery(listQuery.trim());
    }, 250);
    return () => window.clearTimeout(timer);
  }, [listQuery]);

  useEffect(() => {
    if (!isFullscreen) return;
    void loadContactList(1, debouncedListQuery, filterFollowUserids);
  }, [isFullscreen, loadContactList, debouncedListQuery, filterFollowUserids]);

  useEffect(() => {
    if (!isFullscreen) return;
    let cancelled = false;
    void listWecomUsers(accountId)
      .then((payload) => {
        if (!cancelled) setWecomUsers(payload.wecom_users ?? []);
      })
      .catch(() => {
        if (!cancelled) setWecomUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isFullscreen, accountId]);

  useEffect(() => {
    if (!isFullscreen || state.activeContactId == null) {
      setActiveContact(null);
      setContactError(null);
      return;
    }

    setComposerText("");
    let cancelled = false;
    setContactLoading(true);
    setContactError(null);

    void getWecomExternalContact(accountId, state.activeContactId)
      .then((row) => {
        if (!cancelled) setActiveContact(row);
      })
      .catch((caught) => {
        if (cancelled) return;
        setActiveContact(null);
        if (caught instanceof ApiError && caught.status === 404) {
          setContactError("找不到该客户");
        } else {
          setContactError(caught instanceof ApiError ? caught.message : "无法加载客户");
        }
      })
      .finally(() => {
        if (!cancelled) setContactLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isFullscreen, accountId, state.activeContactId]);

  useEffect(() => {
    if (!isFullscreen || !activeContact?.external_userid) {
      setInboundEvents([]);
      setInboundError(null);
      setOutboundMessages([]);
      return;
    }

    let cancelled = false;
    setInboundLoading(true);
    setInboundError(null);

    const uid = activeContact.external_userid;

    void Promise.all([
      listWecomInboundEvents(accountId, { externalUserid: uid, perPage: 50 }),
      listWecomOutboundMessages(accountId, { externalUserid: uid, perPage: 50 }).catch(() => ({
        outbound_messages: [] as WecomOutboundMessage[],
      })),
    ])
      .then(([inboundPayload, outboundPayload]) => {
        if (cancelled) return;
        setInboundEvents(inboundPayload.inbound_events ?? []);
        setOutboundMessages(outboundPayload.outbound_messages ?? []);
      })
      .catch((caught) => {
        if (cancelled) return;
        setInboundEvents([]);
        setOutboundMessages([]);
        setInboundError(caught instanceof ApiError ? caught.message : "无法加载回调事件");
      })
      .finally(() => {
        if (!cancelled) setInboundLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isFullscreen, accountId, activeContact?.external_userid]);

  const hasPendingOutbound = outboundMessages.some((m) => m.status === "pending_confirm");

  // 有「待企微确认」时轻量轮询列表（服务端 index 会按需查企微）；无待确认则停
  useEffect(() => {
    if (!isFullscreen || !activeContact?.external_userid || !hasPendingOutbound) return;

    const uid = activeContact.external_userid;
    const timer = window.setInterval(() => {
      void listWecomOutboundMessages(accountId, { externalUserid: uid, perPage: 50 })
        .then((payload) => {
          setOutboundMessages(payload.outbound_messages ?? []);
        })
        .catch(() => {
          /* 轮询失败忽略，下次再试 */
        });
    }, 45_000);

    return () => {
      window.clearInterval(timer);
    };
  }, [isFullscreen, accountId, activeContact?.external_userid, hasPendingOutbound]);

  // 拉取当前客户在本品牌的绑定计划（真实 steps，非节点预览）
  useEffect(() => {
    if (!isFullscreen || !brandId || !activeContact?.external_userid) {
      setHasActiveBinding(false);
      setPlanSteps([]);
      setJourneyNodes([]);
      setPlanMeta({ journeyId: 0, journeyName: "" });
      setInstructionByStepId({});
      setInstructionByNodeId({});
      setPlanPending(false);
      return;
    }

    let cancelled = false;
    const uid = activeContact.external_userid;
    const resolvedBrandId = brandId;

    async function loadPlan() {
      try {
        const bindingsPayload = await listCustomerBindings(accountId, {
          brandId: resolvedBrandId,
          externalUserid: uid,
        });
        if (cancelled) return;
        const binding = bindingsPayload.customer_bindings?.[0] ?? null;
        if (!binding) {
          setHasActiveBinding(false);
          setPlanSteps([]);
          setJourneyNodes([]);
          setPlanMeta({ journeyId: 0, journeyName: "" });
          setInstructionByStepId({});
          setInstructionByNodeId({});
          setPlanPending(false);
          return;
        }

        const [planPayload, journey] = await Promise.all([
          getCustomerBindingPlan(accountId, binding.id),
          getJourney(accountId, binding.journey_definition_id).catch(() => null),
        ]);
        if (cancelled) return;

        const nodeMap: Record<number, string> = {};
        for (const node of journey?.nodes ?? []) {
          if (node.id == null) continue;
          const text =
            (node.instruction ?? "").trim() || (node.prompt ?? "").trim();
          if (text) nodeMap[node.id] = text;
        }
        setInstructionByNodeId(nodeMap);

        const journeyName = journey?.name ?? `旅程 #${binding.journey_definition_id}`;
        setHasActiveBinding(true);
        setJourneyNodes(
          (journey?.nodes ?? []).flatMap((node) =>
            node.id != null ? [{ id: node.id, offset_days: node.offset_days }] : [],
          ),
        );
        setPlanMeta({
          journeyId: binding.journey_definition_id,
          journeyName,
        });

        const plan = planPayload.plan;
        if (!plan) {
          setPlanSteps([]);
          setInstructionByStepId({});
          setPlanPending(true);
          return;
        }

        setInstructionByStepId(buildInstructionByStepId(plan.steps));
        setPlanSteps(plan.steps ?? []);
        setPlanPending(false);
      } catch (caught) {
        if (cancelled) return;
        setHasActiveBinding(false);
        setPlanSteps([]);
        setJourneyNodes([]);
        setInstructionByStepId({});
        setInstructionByNodeId({});
        setPlanPending(false);
        if (!(caught instanceof JourneyApiError)) {
          /* 静默：计划非对话主路径 */
        }
      }
    }

    void loadPlan();
    return () => {
      cancelled = true;
    };
  }, [
    isFullscreen,
    accountId,
    brandId,
    activeContact?.external_userid,
    planRevision,
  ]);

  // 绑定后规划中：短轮询直到拿到 ready 计划
  useEffect(() => {
    if (!isFullscreen || !planPending) return;
    const timer = window.setInterval(() => {
      setPlanRevision((n) => n + 1);
    }, 4_000);
    return () => window.clearInterval(timer);
  }, [isFullscreen, planPending]);

  const followUserNameByUserid = useMemo(
    () => wecomUserNameByUserid(wecomUsers),
    [wecomUsers],
  );

  const zones = useMemo(() => {
    const enrichedOutbound = outboundMessages.map((message) => {
      if (message.instruction?.trim()) return message;
      const journeyLinked =
        message.journey_plan_step_id != null ||
        message.journey_customer_binding_id != null ||
        message.journey_node_id != null;
      if (!journeyLinked) return message;
      const instruction = lookupTouchInstruction(
        {
          journey_plan_step_id: message.journey_plan_step_id,
          journey_node_id: message.journey_node_id,
        },
        { byStepId: instructionByStepId, byNodeId: instructionByNodeId },
      );
      if (!instruction) return message;
      return { ...message, instruction };
    });
    return timelineZones({
      inboundEvents,
      outboundMessages: enrichedOutbound,
      steps: planSteps,
      nodes: journeyNodes,
      planMeta,
      hasBinding: hasActiveBinding,
      showAllFuture: showAllFuturePlans,
    });
  }, [
    inboundEvents,
    outboundMessages,
    planSteps,
    journeyNodes,
    planMeta,
    hasActiveBinding,
    showAllFuturePlans,
    instructionByStepId,
    instructionByNodeId,
  ]);

  const nextTimelineItems: ChatTimelineItem[] = useMemo(
    () =>
      zones.preview.map((plan) => ({
        kind: "plan" as const,
        at: Date.parse(plan.scheduledAt) || 0,
        plan,
      })),
    [zones.preview],
  );

  // 打开客户 / 时间线更新后滚到底（对齐微信：新消息在下）
  useEffect(() => {
    if (!isFullscreen || inboundLoading) return;
    const el = timelineScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [isFullscreen, inboundLoading, zones.happened, nextTimelineItems, state.activeContactId]);

  const handleSend = useCallback(async () => {
    const text = composerText.trim();
    if (!text || !activeContact || sending) return;
    setSending(true);
    try {
      const message = await createWecomOutboundMessage(accountId, {
        external_contact_id: activeContact.id,
        content: text,
      });
      setOutboundMessages((prev) => [message, ...prev]);
      setComposerText("");
      toast.success("已创建发送任务，请到企微群发助手确认");
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "发送失败");
    } finally {
      setSending(false);
    }
  }, [accountId, activeContact, composerText, sending]);

  const confirmSendNow = useCallback(async () => {
    if (!sendNowPlan?.planStepId || !activeContact || sendNowBusy) return;
    setSendNowBusy(true);
    try {
      const message = await sendJourneyPlanStepNow(accountId, {
        journey_plan_step_id: sendNowPlan.planStepId,
        external_contact_id: activeContact.id,
      });
      setOutboundMessages((prev) => [message, ...prev]);
      setSendNowPlan(null);
      setPlanRevision((n) => n + 1);
      toast.success("已创建企微待确认任务");
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "立即发送失败");
    } finally {
      setSendNowBusy(false);
    }
  }, [accountId, activeContact, sendNowPlan, sendNowBusy]);

  const confirmSchedule = useCallback(async (plannedAtIso: string) => {
    if (!sendNowPlan?.planStepId || !activeContact || sendNowBusy) return;
    setSendNowBusy(true);
    try {
      const result = await schedulePlanStep(accountId, {
        plan_step_id: sendNowPlan.planStepId,
        planned_at: plannedAtIso,
        external_contact_id: activeContact.id,
      });
      setSendNowPlan(null);
      setPlanRevision((n) => n + 1);
      toast.success(
        result.reflowed_count > 0
          ? `已改期，后续 ${result.reflowed_count} 步已按手册调整`
          : "已改期",
      );
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "改期失败");
    } finally {
      setSendNowBusy(false);
    }
  }, [accountId, activeContact, sendNowPlan, sendNowBusy]);

  // 壳层 BackgroundTask / Cable 为真源；离开再进靠 catch-up
  const activeReachDraftsForContact = useMemo(() => {
    if (!activeContact || !backgroundTasks) return [];
    return backgroundTasks.activeReachDrafts({
      externalUserid: activeContact.external_userid,
    });
  }, [activeContact, backgroundTasks]);

  const draftingPlanStepId = useMemo(() => {
    const tasks = backgroundTasks?.allTasks ?? [];
    const userid = activeContact?.external_userid;
    if (pendingDraftStepId != null) {
      return pendingDraftStepId;
    }
    const fromTask = tasks.find((t) =>
      isStepDrafting([t], {
        planStepId: reachDraftScope(t).planStepId,
        externalUserid: userid,
      }),
    );
    return fromTask ? reachDraftScope(fromTask).planStepId : null;
  }, [activeContact, backgroundTasks?.allTasks, pendingDraftStepId]);

  const contactDraftBusy = Boolean(
    activeContact &&
      backgroundTasks &&
      contactHasActiveReachDraft(backgroundTasks.allTasks, activeContact.external_userid),
  );

  // 进行中任务出现后清掉乐观 id；终态时刷新时间线并 toast
  useEffect(() => {
    if (!activeContact || !backgroundTasks) return;
    const userid = activeContact.external_userid;
    const relevant = backgroundTasks.allTasks.filter((t) => {
      if (t.kind !== "reach_draft") return false;
      return reachDraftScope(t).externalUserid === userid;
    });

    for (const task of relevant) {
      const scope = reachDraftScope(task);
      if (task.status === "queued" || task.status === "running") {
        seenActiveReachTaskIdsRef.current.add(task.id);
        if (scope.planStepId != null && pendingDraftStepId === scope.planStepId) {
          setPendingDraftStepId(null);
        }
        continue;
      }

      if (task.status !== "succeeded" && task.status !== "failed") continue;
      if (toastedReachTaskIdsRef.current.has(task.id)) continue;

      const toastKey = scope.planStepId ?? "contact";
      const watched =
        seenActiveReachTaskIdsRef.current.has(task.id) ||
        pendingSuccessToastRef.current.has(toastKey);
      toastedReachTaskIdsRef.current.add(task.id);
      // catch-up 窗口内的旧终态：不重复 toast / 刷新（进页会正常拉 timeline）
      if (!watched) continue;

      setPendingDraftStepId(null);
      setPlanRevision((n) => n + 1);
      void listWecomOutboundMessages(accountId, {
        externalUserid: userid,
        perPage: 50,
      })
        .then((payload) => setOutboundMessages(payload.outbound_messages ?? []))
        .catch(() => {
          /* 计划刷新优先 */
        });

      const successToast =
        pendingSuccessToastRef.current.get(toastKey) ??
        (scope.supersede ? "已重新成稿" : "已立即成稿");
      pendingSuccessToastRef.current.delete(toastKey);
      notifyReachDraftFinished(task, successToast);
    }
  }, [activeContact, backgroundTasks, accountId, pendingDraftStepId]);

  const runReachDraft = useCallback(
    async (opts: {
      planStepId?: number | null;
      supersede: boolean;
      successToast: string;
    }) => {
      if (!activeContact || !accountId) return;
      const stepId = opts.planStepId ?? null;
      if (stepId != null) {
        const already = backgroundTasks?.activeReachDrafts({ planStepId: stepId }) ?? [];
        if (already.length > 0 || pendingDraftStepId === stepId) return;
        setPendingDraftStepId(stepId);
      }
      const toastKey = stepId ?? "contact";
      pendingSuccessToastRef.current.set(toastKey, opts.successToast);
      try {
        // 异步入队；结果经 Cable / catch-up 更新时间线
        await triggerReachOrchestration(accountId, {
          external_contact_id: activeContact.id,
          external_userid: activeContact.external_userid,
          plan_step_id: stepId ?? undefined,
          supersede: opts.supersede,
        });
      } catch (caught) {
        setPendingDraftStepId(null);
        pendingSuccessToastRef.current.delete(toastKey);
        toast.error(caught instanceof ApiError ? caught.message : "成稿失败");
      }
    },
    [accountId, activeContact, backgroundTasks, pendingDraftStepId],
  );

  const handleDraftPlan = useCallback(
    (plan: JourneyPlanItem) => {
      void runReachDraft({
        planStepId: plan.planStepId,
        supersede: false,
        successToast: "已立即成稿",
      });
    },
    [runReachDraft],
  );

  const confirmRedraftPlan = useCallback(async () => {
    if (!redraftPlan?.planStepId) return;
    const stepId = redraftPlan.planStepId;
    setRedraftPlan(null);
    await runReachDraft({
      planStepId: stepId,
      supersede: true,
      successToast: "已重新成稿",
    });
  }, [redraftPlan, runReachDraft]);

  const confirmCancelSend = useCallback(async (mode: "current" | "all") => {
    if (!cancelTarget) return;
    setCancellingOutboundId(cancelTarget.id);
    try {
      await cancelOutboundMessage(accountId, cancelTarget.id, mode);
      toast.success(mode === "all" ? "已取消整批发送" : "已取消当前客户发送");
      setCancelTarget(null);
      setCancelMode(null);
      const payload = await listWecomOutboundMessages(accountId, {
        externalUserid: activeContact?.external_userid ?? cancelTarget.external_userid,
        perPage: 50,
      });
      setOutboundMessages(payload.outbound_messages ?? []);
      setPlanRevision((n) => n + 1);
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "取消发送失败");
    } finally {
      setCancellingOutboundId(null);
    }
  }, [accountId, activeContact, cancelTarget]);

  const requestCancelSend = useCallback(
    async (message: WecomOutboundMessage) => {
      const n = message.group_peer_count ?? 1;
      setCancelPeerCount(n);
      setCancelTarget(message);
      if (n <= 1) setCancelMode("current");
      else setCancelMode(null);
    },
    [],
  );

  const openGroupPeers = useCallback(
    async (message: WecomOutboundMessage) => {
      try {
        const payload = await listOutboundPeers(accountId, message.id);
        setGroupPeers(payload.peers);
        setGroupDrawerOpen(true);
      } catch (caught) {
        toast.error(caught instanceof ApiError ? caught.message : "加载群发名单失败");
      }
    },
    [accountId],
  );

  const confirmReplay = useCallback(async () => {
    if (!replayTarget) return;
    setReplayBusy(true);
    try {
      await replayPlanStep(accountId, {
        source_plan_step_id: replayTarget.planStepId,
        reason: replayTarget.reason,
        external_userid: activeContact?.external_userid,
      });
      toast.success(
        replayTarget.reason === "replay"
          ? "已插入新的执行计划，请成稿后再发送"
          : "已再次安排，请成稿后再发送",
      );
      setReplayTarget(null);
      setPlanRevision((n) => n + 1);
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === "live_step_exists") {
        toast.error(caught.message || "该节点已有未完成的计划步，请先处理接下来里的步骤");
        setReplayTarget(null);
        setPlanRevision((n) => n + 1);
      } else {
        toast.error(caught instanceof ApiError ? caught.message : "插入计划失败");
      }
    } finally {
      setReplayBusy(false);
    }
  }, [accountId, activeContact, replayTarget]);

  function applyPinnedContactLocally(updated: WecomExternalContact) {
    const scrollEl = listScrollRef.current;
    const scrollTop = scrollEl?.scrollTop ?? 0;
    flushSync(() => {
      setActiveContact((prev) => (prev?.id === updated.id ? { ...prev, ...updated } : prev));
      setContacts((prev) => mergeAndSortPinnedContact(prev, updated, "last_activity"));
    });
    restoreElementScrollTop(scrollEl, scrollTop);
  }

  async function togglePin(contact: WecomExternalContact) {
    if (pinSavingId != null) return;
    setPinSavingId(contact.id);
    const nextPinned = !contact.pinned;
    try {
      const updated = await updateWecomExternalContact(accountId, contact.id, {
        pinned: nextPinned,
      });
      // 本地重排并保持左栏 scrollTop，避免 loadContactList 卸列表跳动
      applyPinnedContactLocally(updated);
      toast.success(nextPinned ? "已置顶（账户内共用）" : "已取消置顶");
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "无法更新置顶");
    } finally {
      setPinSavingId(null);
    }
  }

  if (!isFullscreen) return null;

  const listFilterActive = filterFollowUserids.length > 0;
  const emptyListHint = listFilterActive
    ? "当前跟进人筛选下无客户"
    : debouncedListQuery
      ? "无匹配客户"
      : "暂无客户";

  return (
    <div
      className="fixed inset-[8px] z-40 flex flex-col rounded-lg border border-border bg-background shadow-lg"
      role="dialog"
      aria-modal="true"
      aria-label="客户聊天"
    >
      <header className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1">
          <WecomUserMultiSelect
            accountId={accountId}
            variant="header"
            value={filterFollowUserids}
            onChange={setFilterFollowUserids}
            onUsersResolved={mergeWecomUsers}
            emptyLabel="全部跟进人"
          />
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="最小化"
            onClick={minimize}
          >
            <Minimize2 className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="关闭"
            onClick={closeActive}
          >
            <X className="size-4" />
          </Button>
        </div>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-[260px_minmax(0,1fr)_360px]">
        {/* 左栏：联系人列表（最近入站 + 置顶） */}
        <aside className="flex min-h-0 flex-col border-r border-border">
          <div className="shrink-0 space-y-2 border-b border-border px-3 py-2">
            <div>
              <p className="text-sm font-medium">
                客户
                <span className="font-normal text-muted-foreground"> · {listTotal}</span>
              </p>
            </div>
            <div className="relative">
              <Search
                className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                type="search"
                value={listQuery}
                onChange={(e) => setListQuery(e.target.value)}
                placeholder="搜昵称或消息"
                aria-label="搜索客户"
                className="h-8 rounded-md bg-muted/40 pr-8 pl-8 text-sm [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
              />
              {listQuery ? (
                <button
                  type="button"
                  aria-label="清除搜索"
                  className="absolute top-1/2 right-1.5 flex size-5 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setListQuery("");
                    setDebouncedListQuery("");
                  }}
                >
                  <X className="size-3.5" />
                </button>
              ) : null}
            </div>
          </div>
          <div ref={listScrollRef} className="min-h-0 flex-1 overflow-y-auto">
            {listLoading ? (
              <p className="px-3 py-4 text-sm text-muted-foreground">正在加载…</p>
            ) : listError ? (
              <p className="px-3 py-4 text-sm text-destructive" role="alert">
                {listError}
              </p>
            ) : contacts.length === 0 ? (
              <p className="px-3 py-4 text-sm text-muted-foreground">{emptyListHint}</p>
            ) : (
              contacts.map((contact) => {
                const active = contact.id === state.activeContactId;
                const isPinned = Boolean(contact.pinned);
                return (
                  <div
                    key={contact.id}
                    className={cn(
                      "group flex w-full items-stretch border-l-2 border-transparent transition-colors hover:bg-muted/50",
                      active && "border-l-primary bg-primary/10 hover:bg-primary/10",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        switchContact({
                          id: contact.id,
                          name: contact.name,
                          avatar_url: contact.avatar_url,
                        })
                      }
                      className="flex min-w-0 flex-1 items-center gap-3 px-3 py-3 text-left"
                    >
                      <ContactAvatar name={contact.name} avatarUrl={contact.avatar_url} />
                      <div className="min-w-0 flex-1">
                        <p
                          className={cn(
                            "flex min-w-0 items-center gap-1 truncate text-sm font-medium",
                            active && "font-semibold text-foreground",
                          )}
                        >
                          {isPinned ? (
                            <Pin className="size-3 shrink-0 text-primary" aria-hidden />
                          ) : null}
                          <span className="truncate">{contact.name || "未获取昵称"}</span>
                          <ContactChurnedBadge
                            churnedAt={contact.churned_at}
                            className="h-4 px-1.5 text-[10px]"
                          />
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {formatLastInboundHint(contact.last_inbound_at, "暂无消息")}
                        </p>
                      </div>
                    </button>
                    <div className="flex shrink-0 items-center pr-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            size="icon-sm"
                            variant="ghost"
                            aria-label="客户操作"
                            disabled={pinSavingId === contact.id}
                          >
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-36">
                          <DropdownMenuItem
                            onClick={() => void togglePin(contact)}
                            disabled={pinSavingId === contact.id}
                          >
                            {isPinned ? "取消置顶" : "置顶"}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          {listTotalPages > 1 ? (
            <div className="flex shrink-0 items-center justify-between border-t border-border px-3 py-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={listPage <= 1 || listLoading}
                onClick={() =>
                  void loadContactList(listPage - 1, debouncedListQuery, filterFollowUserids)
                }
              >
                上一页
              </Button>
              <span className="text-xs text-muted-foreground">
                {listPage} / {listTotalPages}
              </span>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={listPage >= listTotalPages || listLoading}
                onClick={() =>
                  void loadContactList(listPage + 1, debouncedListQuery, filterFollowUserids)
                }
              >
                下一页
              </Button>
            </div>
          ) : null}
        </aside>

        {/* 中栏：时间线（入站 + 临时发送 + 未来计划）+ 发送框 */}
        <main className="flex min-h-0 min-w-0 flex-col">
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-2">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">
                {activeContact?.name || "对话"}
              </p>
              <p className="text-xs text-muted-foreground">
                旧消息在上 · 新消息在下
                {planPending
                  ? " · 计划生成中…"
                  : zones.queue.length > 0
                    ? showAllFuturePlans
                      ? ` · 接下来全部 ${zones.queue.length} 条`
                      : ` · 接下来 ${zones.preview.length} / ${zones.queue.length}`
                    : zones.emptyCopy
                      ? ` · ${zones.emptyCopy}`
                      : null}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Label
                htmlFor="show-all-future-plans"
                className="cursor-pointer text-xs text-muted-foreground"
              >
                显示全部未来计划
              </Label>
              <Switch
                id="show-all-future-plans"
                checked={showAllFuturePlans}
                onCheckedChange={setShowAllFuturePlans}
                size="sm"
                aria-label="显示全部未来旅程计划"
              />
            </div>
          </div>

          <div ref={timelineScrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
            {inboundLoading ? (
              <p className="py-12 text-center text-sm text-muted-foreground">加载中…</p>
            ) : null}
            {inboundError ? (
              <p
                className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                role="alert"
              >
                {inboundError}
              </p>
            ) : null}
            {!inboundLoading && !inboundError ? (
              <div className="space-y-6">
                {planPending ? (
                  <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                    计划仍在生成，完成后会自动刷新
                  </p>
                ) : null}
                <section>
                  <h2 className="mb-2 text-xs font-medium text-muted-foreground">已发生</h2>
                  {zones.happened.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border px-6 py-8 text-center">
                      <p className="text-sm font-medium text-foreground">还没有消息</p>
                      <p className="text-xs text-muted-foreground">
                        客户消息、临时发送、旅程触达会出现在这里
                      </p>
                    </div>
                  ) : (
                    <InboundEventTimeline
                      items={zones.happened}
                      contactChurnedAt={activeContact?.churned_at ?? null}
                      onCancelSend={(msg) => void requestCancelSend(msg)}
                      onReschedule={({ planStepId }) =>
                        setReplayTarget({ planStepId, reason: "reschedule" })
                      }
                      onReplay={({ planStepId }) =>
                        setReplayTarget({ planStepId, reason: "replay" })
                      }
                      onOpenGroupPeers={(msg) => void openGroupPeers(msg)}
                      cancellingOutboundId={cancellingOutboundId}
                    />
                  )}
                </section>
                <section>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <h2 className="text-xs font-medium text-muted-foreground">接下来</h2>
                    {zones.queue.length > 0 ? (
                      <span className="text-xs text-muted-foreground">
                        {zones.preview.length} / {zones.queue.length}
                      </span>
                    ) : null}
                  </div>
                  {zones.emptyCopy ? (
                    <p className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                      {zones.emptyCopy}
                    </p>
                  ) : (
                    <InboundEventTimeline
                      items={nextTimelineItems}
                      nextStepId={zones.nextStepId}
                      onSendPlanNow={setSendNowPlan}
                      sendingPlanStepId={sendNowBusy ? sendNowPlan?.planStepId ?? null : null}
                      onDraftPlan={handleDraftPlan}
                      onRedraftPlan={setRedraftPlan}
                      draftingPlanStepId={draftingPlanStepId}
                      contactDraftBusy={contactDraftBusy}
                    />
                  )}
                </section>
              </div>
            ) : null}
          </div>

          <div className="shrink-0 border-t border-border p-3">
            <div className="flex items-end gap-2">
              <Textarea
                value={composerText}
                onChange={(e) => setComposerText(e.target.value)}
                placeholder={
                  activeContact
                    ? "写一条临时消息发给该客户…"
                    : "请先选择客户"
                }
                disabled={!activeContact || sending}
                rows={2}
                className="min-h-[2.75rem] flex-1 resize-none text-sm"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    void handleSend();
                  }
                }}
              />
              <Button
                type="button"
                size="sm"
                disabled={!activeContact || !composerText.trim() || sending}
                onClick={() => void handleSend()}
                aria-label="发送消息"
              >
                <Send className="size-4" data-icon="inline-start" />
                {sending ? "发送中…" : "发送"}
              </Button>
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">
              对当前客户临时发送 · 创建后需在企微「群发助手」确认才会发出 · ⌘/Ctrl+Enter
            </p>
          </div>
        </main>

        {/* 右栏：资料 | Hermes */}
        <aside className="flex min-h-0 min-w-0 flex-col border-l border-border">
          <div
            role="tablist"
            aria-label="右栏视图"
            className="flex shrink-0 gap-1 border-b border-border px-3 pt-2"
          >
            {(
              [
                { key: "profile" as const, label: "客户资料" },
                { key: "hermes" as const, label: "Hermes" },
              ] as const
            ).map((item) => {
              const active = state.rightTab === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setRightTab(item.key)}
                  className={cn(
                    "-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors",
                    active
                      ? "border-foreground text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  )}
                >
                  {item.label}
                </button>
              );
            })}
          </div>

          {state.rightTab === "profile" ? (
            <CustomerProfilePanel
              contact={activeContact}
              isLoading={contactLoading}
              error={contactError}
              brandId={brandId}
              followUserNameByUserid={followUserNameByUserid}
              onBindingChange={() => setPlanRevision((n) => n + 1)}
              onContactChange={(row) => {
                if (
                  activeContact &&
                  Boolean(activeContact.pinned) !== Boolean(row.pinned)
                ) {
                  // 资料面板改置顶：本地重排并保左栏滚动
                  applyPinnedContactLocally(row);
                  return;
                }
                setActiveContact(row);
                setContacts((prev) => prev.map((c) => (c.id === row.id ? row : c)));
              }}
            />
          ) : (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <HermesDrawer variant="embedded" />
            </div>
          )}
        </aside>
      </div>

      <SendPlanStepDialog
        open={Boolean(sendNowPlan)}
        onOpenChange={(open) => {
          if (!open && !sendNowBusy) setSendNowPlan(null);
        }}
        confirming={sendNowBusy}
        onConfirmNow={() => void confirmSendNow()}
        onConfirmSchedule={(iso) => void confirmSchedule(iso)}
        currentStepId={sendNowPlan?.planStepId ?? null}
        steps={planSteps}
        nodes={journeyNodes}
        inFlightStepIds={outboundMessages
          .filter((m) => m.status === "pending_confirm" && m.journey_plan_step_id != null)
          .map((m) => m.journey_plan_step_id!)}
        sentStepIds={outboundMessages
          .filter((m) => m.status === "sent" && m.journey_plan_step_id != null)
          .map((m) => m.journey_plan_step_id!)}
      />

      <ConfirmDialog
        open={Boolean(redraftPlan)}
        onOpenChange={(open) => {
          if (!open && draftingPlanStepId == null) setRedraftPlan(null);
        }}
        title="重新成稿？"
        description="将重新跑分类与成稿并覆盖本步客户话术。不会自动推到企微，成稿后请再点「立即发送」。"
        confirmLabel="确认重新成稿"
        onConfirm={() => void confirmRedraftPlan()}
      />

      <ConfirmDialog
        open={cancelTarget != null && cancelPeerCount <= 1}
        onOpenChange={(open) => {
          if (!open && cancellingOutboundId == null) {
            setCancelTarget(null);
            setCancelMode(null);
          }
        }}
        title="取消发送？"
        description="将停止企微群发助手里的任务，本条标为已取消。已发出的消息无法撤回。"
        confirmLabel="取消发送"
        tone="destructive"
        confirming={cancellingOutboundId != null}
        onConfirm={() => void confirmCancelSend("current")}
      />

      <Dialog
        open={cancelTarget != null && cancelPeerCount > 1 && cancelMode == null}
        onOpenChange={(open) => {
          if (!open && cancellingOutboundId == null) {
            setCancelTarget(null);
            setCancelMode(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>取消群发 {cancelPeerCount} 人？</DialogTitle>
            <DialogDescription>
              企微只能整单停止，不能只改一个人。选「只取消当前客户」会先停旧任务，再给其余人重建同文待确认。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              type="button"
              variant="outline"
              disabled={cancellingOutboundId != null}
              onClick={() => void confirmCancelSend("current")}
            >
              只取消当前客户
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={cancellingOutboundId != null}
              onClick={() => void confirmCancelSend("all")}
            >
              全部取消
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setCancelTarget(null);
                setCancelMode(null);
              }}
            >
              返回
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={replayTarget != null}
        onOpenChange={(open) => {
          if (!open && !replayBusy) setReplayTarget(null);
        }}
        title={replayTarget?.reason === "replay" ? "再一次执行计划？" : "再次安排？"}
        description="将插入一条新的未来任务，不会改已有记录。插入后请先成稿再发送。"
        confirmLabel="确认插入"
        confirming={replayBusy}
        onConfirm={() => void confirmReplay()}
      />

      <GroupSendRecipientsDrawer
        open={groupDrawerOpen}
        onOpenChange={setGroupDrawerOpen}
        peers={groupPeers}
        customersBase={`/accounts/${accountId}/brands/${brandId}/apps/journey/customers`}
      />
    </div>
  );
}
